import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const NAV = [
  {
    group: 'Masters',
    items: [
      { to: '/masters', label: 'Masters home', key: 'masters_home' },
      { to: '/masters/divisions', label: 'Division master', key: 'masters_divisions' },
      { to: '/masters/factories', label: 'Factory master', key: 'masters_factories' },
      { to: '/masters/shift-lines', label: 'Shift-line master', key: 'masters_shiftlines' },
      { to: '/masters/machines', label: 'Machine master', key: 'masters_machines' },
      { to: '/masters/operations', label: 'Operation master', key: 'masters_operations' },
      { to: '/masters/costing', label: 'Costing master', key: 'costing' },
      { to: '/masters/fund-groups', label: 'Fund. operation master', key: 'masters_fundgroups' },
      { to: '/masters/employees', label: 'Employee master', key: 'masters_employees' },
      { to: '/masters/downtime-reasons', label: 'Downtime reason master', key: 'masters_downtime_reasons' },
    ]
  },
  {
    group: 'Planning',
    items: [
      { to: '/ob', label: 'Operation bulletin', key: 'ob' },
      { to: '/line-design', label: 'Line design', key: 'line_design' },
      { to: '/monthly-summary', label: 'Monthly summary', key: 'monthly_summary' },
    ]
  },
  {
    group: 'Operations',
    items: [
      { to: '/deployment', label: 'Deployment', key: 'deployment' },
      { to: '/daily-balancing', label: 'Daily balancing', key: 'daily_balancing' },
      { to: '/hourly', label: 'Hourly reporting', key: 'hourly_reporting' },
    ]
  },
  {
    group: 'Reports',
    items: [
      { to: '/skill-matrix', label: 'Skill matrix', key: 'skill_matrix' },
      { to: '/skill-journey', label: 'Skill journey', key: 'skill_journey' },
    ]
  },
  {
    group: 'Dashboards',
    items: [
      { to: '/dashboard/supervisor', label: 'Supervisor dashboard', key: 'dashboard_supervisor' },
      { to: '/dashboard/plant', label: 'Plant head dashboard', key: 'dashboard_plant' },
      { to: '/dashboard/downtime', label: 'Downtime dashboard', key: 'dashboard_downtime' },
      { to: '/plant-optimiser', label: 'Plant optimiser', key: 'plant_optimiser' },
    ]
  },
];

function Dot() {
  return (
    <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor', opacity: 0.4, flexShrink: 0, display: 'inline-block' }} />
  );
}

export default function Layout({ children }) {
  const { user, logout, hasAccess } = useAuth();
  const navigate = useNavigate();
  const initials = user?.name ? user.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : 'LC';
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const visibleNav = NAV
    .map(section => ({ ...section, items: section.items.filter(item => hasAccess(item.key)) }))
    .filter(section => section.items.length > 0);
  const showUserAccess = !!user?.is_super_admin;
  const showRoleMaster = !!user?.is_super_admin;

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* NAV */}
      <div style={{ width: 212, minWidth: 212, display: 'flex', flexDirection: 'column', background: 'var(--bg-secondary)', borderRight: '0.5px solid var(--border-md)' }}>
        {/* Logo */}
        <div style={{ padding: '16px 18px 12px', borderBottom: '0.5px solid var(--border)' }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)' }}>
            <span style={{ color: 'var(--acc)' }}>Skill</span>flo
          </div>

        </div>

        {/* Nav items */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
          {visibleNav.map((section, si) => (
            <React.Fragment key={section.group}>
              {si > 0 && section.group === 'Planning' && (
                <div style={{ height: '0.5px', background: 'var(--border)', margin: '4px 0' }} />
              )}
              <div style={{ fontSize: 9.5, fontWeight: 500, letterSpacing: '0.8px', color: 'var(--text-tertiary)', padding: '7px 16px 3px', textTransform: 'uppercase' }}>
                {section.group}
              </div>
              {section.items.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                >
                  <Dot />
                  {item.label}
                </NavLink>
              ))}
              {section.group === 'Dashboards' && (
                <div style={{ height: '0.5px', background: 'var(--border)', margin: '4px 0' }} />
              )}
            </React.Fragment>
          ))}
          {(showUserAccess || showRoleMaster) && (
            <div style={{ fontSize: 9.5, fontWeight: 500, letterSpacing: '0.8px', color: 'var(--text-tertiary)', padding: '7px 16px 3px', textTransform: 'uppercase' }}>Admin</div>
          )}
          {showRoleMaster && (
            <NavLink to="/admin/roles" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
              <Dot />Role master
            </NavLink>
          )}
          {showUserAccess && (
            <NavLink to="/admin/users" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
              <Dot />User access
            </NavLink>
          )}
        </div>

        {/* User footer */}
        <div style={{ padding: '10px 16px', borderTop: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--acc-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 500, color: 'var(--acc-t)', flexShrink: 0 }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
            <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{user?.role}</div>
          </div>
          <button onClick={() => { logout(); navigate('/login'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 12 }} title="Logout">⏻</button>
        </div>
      </div>

      {/* MAIN */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* Topbar */}
        <div style={{ height: 46, borderBottom: '0.5px solid var(--border-md)', display: 'flex', alignItems: 'center', padding: '0 18px', gap: 12, flexShrink: 0, background: 'var(--bg-primary)' }}>
          <div style={{ flex: 1, fontSize: 12.5, fontWeight: 500, color: 'var(--text-primary)' }} id="lc-page-title">Skillflo</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
            {now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · {now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {children}
        </div>
      </div>
    </div>
  );
}